import { injectable, Container, optional } from "inversify";
import { injectToken, Token, TokenContainerModule, multiInjectToken } from "inversify-token";
import { YinzCamInjectModule } from 'yinzcam-inject';
import { JanusModeContextManager, JanusModeContextManagerToken } from "./mode";
import { BufferManager, ManualPassthrough, ReactiveMicrocomponent, SimpleSink } from 'yinzcam-rma';
import { BufferedPassthrough } from "yinzcam-rma";
import loader from '@beyonk/async-script-loader';
import _ from "lodash";
import { DateTime } from "luxon";

const JanusAnalyticsStateManagerToken = new Token<JanusAnalyticsStateManager>(Symbol.for("JanusAnalyticsStateManager"));
const JanusAnalyticsServiceProviderToken = new Token<JanusAnalyticsServiceProvider>(Symbol.for("JanusAnalyticsServiceProvider"));

export const JanusAnalyticsManagerToken = new Token<JanusAnalyticsManager>(Symbol.for("JanusAnalyticsManager"));

export const JanusAnalyticsManagerModule: YinzCamInjectModule = new YinzCamInjectModule((container: Container): void => {
  container.load(new TokenContainerModule((bindToken) => {
    for (const key in CONFIG.analyticsProviderClassNames) {
      const name = CONFIG.analyticsProviderClassNames[key];
      const clazz = (<any>ServiceProviders)[name];
      bindToken(JanusAnalyticsServiceProviderToken).to(clazz);
    }
    bindToken(JanusAnalyticsStateManagerToken).to(JanusAnalyticsStateManager).inSingletonScope();
    bindToken(JanusAnalyticsManagerToken).to(JanusAnalyticsManager).inSingletonScope();
  }));
});

interface JanusAnalyticsEvent {
  meta: {
    page: {
      path: string,
      name: string,
      content?: string
    },
    timestamp: number
  },
  category: string,
  type: string,
  detail?: string,
  context?: Record<string, any>,
}

export interface JanusAnalyticsPageContext {
  sendPageView();
  sendInteractionEvent(type: string, detail?: string, context?: Record<string, any>): void;
  validate(path?: string, name?: string, content?: string): boolean;
}

interface JanusAnalyticsServiceProvider {
  connectToManager(eventSource: ReactiveMicrocomponent<JanusAnalyticsEvent>);
  onTrackingConsentChange(optIn: boolean);
}

// this is stored outside of the manager class to avoid circular dependencies
@injectable()
class JanusAnalyticsStateManager {
  public readonly appId: string;
  public readonly eventSource: ManualPassthrough<JanusAnalyticsEvent>;

  public constructor(
    @injectToken(JanusModeContextManagerToken) private readonly modeManager: JanusModeContextManager) {
    this.appId = `${CONFIG.league}_${CONFIG.tricode}`.toUpperCase();
    this.eventSource = new ManualPassthrough({
      name: 'JanusAnalyticsManager:eventSource',
      saveToLocalStorage: false,
      bufferInputs: true
    });
  }
}

@injectable()
export class JanusAnalyticsManager {
  private readonly dummySp: ServiceProviders.DummyJanusAnalyticsServiceProvider;

  public constructor (
    @injectToken(JanusAnalyticsStateManagerToken) private readonly stateManager: JanusAnalyticsStateManager,
    @multiInjectToken(JanusAnalyticsServiceProviderToken) @optional() private readonly serviceProviders: JanusAnalyticsServiceProvider[]) {
    // If service providers were specified connect them to the source; otherwise, connect a dummy provider to drain the source.
    if (!serviceProviders?.length) {
      console.warn("JanusAnalyticsManager: no service providers configured, all analytics events will be discarded");
      this.serviceProviders = [ new ServiceProviders.DummyJanusAnalyticsServiceProvider() ];
    }
    for (const sp of this.serviceProviders) {
      sp.connectToManager(this.stateManager.eventSource);
    }
  }

  public createPageContext(path?: string, name?: string, content?: string): JanusAnalyticsPageContext {
    return new InternalJanusAnalyticsPageContext(this.stateManager, path, name, content);
  }

  public onTrackingConsentChange(optIn: boolean) {
    for (const sp of this.serviceProviders) {
      sp.onTrackingConsentChange(optIn);
    }
  }
}

class InternalJanusAnalyticsPageContext implements JanusAnalyticsPageContext {
  public constructor(
    private readonly stateManager: JanusAnalyticsStateManager,
    private readonly path: string,
    private readonly name: string,
    private readonly content?: string) {
      this.path = this.path || location.pathname;
      this.name = this.name || document.title;  
  }

  sendPageView(): void {
    this.postEvent("navigation", "page_view");
  }

  sendInteractionEvent(type: string, detail?: string, context?: Record<string, any>): void {
    this.postEvent("interaction", type, detail, context);
  }

  validate(path?: string, name?: string, content?: string): boolean {
    return path === this.path && name === this.name && content === this.content;
  }

  private postEvent(category: string, type: string, detail?: string, context?: Record<string, any>): void {
    // This gets immediately consumed by downstream buffers.
    let fullEvent: JanusAnalyticsEvent = {
      meta: {
        page: {
          path: this.path,
          name: this.name,
          content: this.content
        },
        timestamp: Date.now()
      },
      category,
      type,
      detail,
      context,
    };
    this.stateManager.eventSource.setValue(fullEvent);
  }
}

namespace ServiceProviders {
  export class DummyJanusAnalyticsServiceProvider implements JanusAnalyticsServiceProvider {
    private eventSink: SimpleSink<[JanusAnalyticsEvent]> | null;

    public connectToManager(eventSource: ReactiveMicrocomponent<JanusAnalyticsEvent>) {
      this.eventSink = new SimpleSink({
        name: `DummyJanusAnalyticsServiceProvider_eventSink`,
        saveToLocalStorage: false
      }, async (e: JanusAnalyticsEvent) => {
        // discard
      }, eventSource);
    }

    public onTrackingConsentChange(optIn: boolean) {
      return; // nothing to do
    }
  }

  abstract class AbstractBufferingJanusAnalyticsServiceProvider implements JanusAnalyticsServiceProvider {
    private eventBuffer: BufferedPassthrough<JanusAnalyticsEvent> | null;
    private eventSink: SimpleSink<[BufferManager<JanusAnalyticsEvent>]> | null;
    private initialized: boolean;
    private failed: boolean;
    private bufferManager: BufferManager<JanusAnalyticsEvent> | null;
  
    protected constructor(protected readonly name: string) {
      this.eventBuffer = null;
      this.eventSink = null;
      this.initialized = false;
      this.failed = false;
      this.bufferManager = null;
    }
  
    public connectToManager(eventSource: ReactiveMicrocomponent<JanusAnalyticsEvent>) {
      // Connect pipeline
      this.eventBuffer = new BufferedPassthrough({
        name: `AbstractJanusAnalyticsServiceProvider:${this.name}_eventBuffer`,
        bufferMaxLength: 100
      }, eventSource);
      this.eventSink = new SimpleSink({
        name: `AbstractJanusAnalyticsServiceProvider:${this.name}_eventSink`,
        saveToLocalStorage: false
      }, async (bm: BufferManager<JanusAnalyticsEvent>) => {
        this.bufferManager = bm;
        if (this.initialized) {
          await this.eventsAvailable(this.bufferManager.getLength());
        }
      }, this.eventBuffer);
  
      // Initialize pipeline
      this.initialize()
      .then(() => {
        // console.info(`AbstractJanusAnalyticsServiceProvider:${this.name}: initialized successfully`);
        this.initialized = true;
      })
      .catch((reason) => {
        console.error(`AbstractJanusAnalyticsServiceProvider:${this.name}: initialization failed - events will not be processed by this service provider`, reason);
        this.failed = true;
      })
      .finally(() => {
        if (this.bufferManager && !this.failed) {
          this.eventsAvailable(this.bufferManager.getLength());
        }
      });
    }

    public onTrackingConsentChange(optIn: boolean) {
      this.handleTrackingConsentChange(optIn);
    }
  
    protected consumeAllEvents(): JanusAnalyticsEvent[] {
      return (this.bufferManager)? this.bufferManager.consume() : [];
    }
  
    protected abstract initialize(): Promise<void>;
  
    protected abstract eventsAvailable(count: number): Promise<void>;

    protected handleTrackingConsentChange(optIn: boolean) {
      return; // by default, do nothing
    }
  }
  
  @injectable()
  export class GoogleAnalyticsServiceProvider extends AbstractBufferingJanusAnalyticsServiceProvider {
    private static readonly GA_LOCAL_STORAGE_KEY = 'ga:clientId';
  
    private readonly scriptScheme: string;
    private readonly properties: string[];
    private readonly configurations: any;
  
    public constructor() {
      super('GoogleAnalytics');
      this.scriptScheme = 'https://';
      this.properties = []; //['UA-188593099-1'];
      this.configurations = {};
    }
  
    protected initialize(): Promise<void> {
      function test() {
        //console.log(`GoogleAnalyticsServiceProvider.process: test`);
        const ga = window['ga'];
        return Array.isArray(ga?.q);
      }
    
      function callback(this: any, resolve, reject) {
        try {
          this.properties.forEach(p => {
            let config = this.configurations[p] || {};
            config['storage'] = 'none';
            config['clientId'] = localStorage.getItem(GoogleAnalyticsServiceProvider.GA_LOCAL_STORAGE_KEY);
            const ga = window['ga'];
            ga('create', p, config);
            ga(function (tracker) {
              localStorage.setItem(GoogleAnalyticsServiceProvider.GA_LOCAL_STORAGE_KEY, tracker.get('clientId'));
            });
            ga('set', 'checkProtocolTask', function () { /* nothing */ });
            resolve();
            /*
            let page = path;
            if (params) {
              page = `${path}?${new URLSearchParams(params).toString()}`;
            }
            ga('set', 'page', page);
            ga('send', 'pageview');
            */
          });
        } catch (e) {
          reject(e);
        }
      }
  
      window['ga'] = window['ga'] || function() {
        (window['ga'].q = window['ga'].q || []).push(arguments);
      };
      window['ga'].l = window['ga'].l || Date.now()
      return new Promise<void>((resolve, reject) => {
        loader([{
          type: 'script',
          url: `${this.scriptScheme}www.google-analytics.com/analytics.js`
        }], test, callback.bind(this, resolve, reject));
      });
    }
  
    protected async eventsAvailable(count: number): Promise<void> {
      const events = this.consumeAllEvents();
      // NOT IMPLEMENTED!    
    }
  }

  interface GoogleTagManagerDataObject {
    [key: string]: string | GoogleTagManagerDataObject;
  }
  
  @injectable()
  export class GoogleTagManagerServiceProvider extends AbstractBufferingJanusAnalyticsServiceProvider {
    private readonly scriptScheme: string;
    protected readonly containerId: string;
    protected readonly dataLayer: any[];
    protected readonly dataObject: GoogleTagManagerDataObject;
    private userId: string | null;
  
    public constructor(private readonly pushFullDataObject = false, private readonly expandDotNotationKeys = false) {
      super('GoogleTagManager');
      this.scriptScheme = '//'; // could override to https:// for use in Cordova, but better to use Cordova plugin instead?
      this.containerId = CONFIG.gtmContainerId; //'GTM-TLJ6SCM';
      this.dataLayer = [];
      this.dataObject = {};
      this.userId = window.localStorage?.getItem('janus_analytics_user_id');
    }

    protected updateDataLayer(vars: any) {
      const updateObj = (this.pushFullDataObject)? this.dataObject : {};
      for (let key in vars) {
        if (this.expandDotNotationKeys && key.includes('.')) {
          const parts = key.split('.');
          const obj = parts.slice(0, -1).reduce((o, i) => o[i] = o[i] || {}, updateObj);
          obj[parts[parts.length - 1]] = vars[key];
        } else {
          updateObj[key] = vars[key];
        }
      }
      //console.log('UPDATE DATA LAYER', JSON.stringify(updateObj, null, 2));
      this.dataLayer.push(updateObj);
    }
  
    protected async initialize(): Promise<void> {
      const test = () => {
        return _.isObjectLike((<any>window).google_tag_manager);
      }
    
      const callback = (resolve, reject) => {
        try {
          this.initializeGoogleTagManager().then(resolve).catch(reject);
        } catch (e) {
          console.error(`GoogleTagManagerServiceProvider.process: failed to initialize`, e);
          reject(e);
        }
      }
  
      const l = 'dataLayer';
      const w = window;
      w[l] = this.dataLayer;
      this.dataLayer.push({'gtm.start': new Date().getTime(), event: 'gtm.js'});
      await this.initializeDataLayer();
      return new Promise<void>((resolve, reject) => {
        setTimeout(() => {
          const disableGoogleTagManagerInAppMode = CONFIG.disableGoogleTagManagerInAppMode;
          const appQSParam = CONFIG.appModeQueryStringParameter;
          const qsParams = new URLSearchParams(window.location.search);
          if (!disableGoogleTagManagerInAppMode || qsParams.get(appQSParam) !== "true") {
            loader([{
              type: 'script',
              url: `${this.scriptScheme}www.googletagmanager.com/gtm.js?id=${this.containerId}`
            }], test, callback.bind(this, resolve, reject));  
          }
        }, 1000);
      });
    }

    protected getUserDataLayerVars(): any {
      const vars: any = {};
      if (this.userId) {
        vars['userId'] = this.userId;
        vars['loggedIn'] = true;
      } else {
        vars['userId'] = null;
        vars['loggedIn'] = false;
      }
      return vars;
    }
  
    // for subclasses to override with their own data layer code
    protected async initializeDataLayer(): Promise<void> {
      this.updateDataLayer(this.getUserDataLayerVars());
    }

    protected async initializeGoogleTagManager(): Promise<void> {
      // Do nothing
    }
  
    protected async eventsAvailable(count: number): Promise<void> {
      const events = this.consumeAllEvents();
      for (const event of events) {
        try {
          await this.processEvent(event);
        } catch (e) {
          console.error('GoogleTagManagerServiceProvider.eventsAvailable: exception in processEvent', e);
        }
      }
    }

    protected async processEvent(event: JanusAnalyticsEvent): Promise<void> {
      switch (event.category) {
        case "navigation": {
          switch (event.type) {
            case "page_view": {
              this.updateDataLayer({
                ...(this.getUserDataLayerVars()),
                "event": "pageviewCustomEvent",
                "pagePath": event.meta.page.path,
                "pageTitle": event.meta.page.name,
                "pageContent": event.meta.page.content
              });
              break;
            }
            /*
            case "interaction":
              this.updateDataLayer({
                "event": "interactionCustomEvent",
                "pagePath": event.meta.page.path,
                "pageTitle": event.meta.page.name,
                "pageContent": event.meta.page.content,
                "interactionType": event.type,
                "interactionTargetId": event.detail || ''
              });
              break;
            */
          }
          break;
        }
        case "interaction": {
          this.updateDataLayer({
            ...(this.getUserDataLayerVars()),
            "event": "interactionCustomEvent",
            "pagePath": event.meta.page.path,
            "pageTitle": event.meta.page.name,
            "pageContent": event.meta.page.content,
            "interactionType": event.type,
            "interactionTargetId": event.detail || ''
          });
          switch (event.type) {
            case 'login': {
              this.userId = event.detail || null;
              window.localStorage?.setItem('janus_analytics_user_id', this.userId || '');
              this.updateDataLayer(this.getUserDataLayerVars());
              break;
            }
            case 'logout': {
              this.userId = null;
              window.localStorage?.removeItem('janus_analytics_user_id');
              this.updateDataLayer(this.getUserDataLayerVars());
              break;
            }
          }
          break;
        }
      }
    }
  }

  /*
    <!-- Google tag (gtag.js) -->
  <script async src="https://www.googletagmanager.com/gtag/js?id=TAG_ID"></script>
  <script>
    window.dataLayer = window.dataLayer || [];

  </script>
  */
  @injectable()
  export class GTagServiceProvider extends AbstractBufferingJanusAnalyticsServiceProvider {
    protected readonly scriptScheme: string;
    protected readonly tagId: string;
    protected readonly dataLayer: any[];
    protected readonly dataObject: GoogleTagManagerDataObject;
  
    public constructor(private readonly pushFullDataObject = false, private readonly expandDotNotationKeys = false) {
      super('GoogleTagManager');
      this.scriptScheme = '//';
      this.tagId = CONFIG.gtagId;
      this.dataLayer = [];
      this.dataObject = {};
    }

    protected updateDataLayer(vars: any) {
      const updateObj = (this.pushFullDataObject)? this.dataObject : {};
      for (let key in vars) {
        if (this.expandDotNotationKeys && key.includes('.')) {
          const parts = key.split('.');
          const obj = parts.slice(0, -1).reduce((o, i) => o[i] = o[i] || {}, updateObj);
          obj[parts[parts.length - 1]] = vars[key];
        } else {
          updateObj[key] = vars[key];
        }
      }
      //console.log('UPDATE DATA LAYER', JSON.stringify(updateObj, null, 2));
      this.dataLayer.push(updateObj);
    }
  
    protected async initialize(): Promise<void> {
      const test = () => {
        return _.isObjectLike((<any>window).gtag);
      }
    
      const callback = (resolve, reject) => {
        try {
          this.initializeGTag().then(resolve).catch(reject);
        } catch (e) {
          console.error(`GoogleTagManagerServiceProvider.process: failed to initialize`, e);
          reject(e);
        }
      }
  
      const l = 'dataLayer';
      const w: any = window;
      w[l] = this.dataLayer;
      this.dataLayer.push({'gtm.start': new Date().getTime(), event: 'gtm.js'});
      await this.initializeDataLayer();
      const gtag = (...args) => {
        this.dataLayer.push(args);
      }
      w['gtag'] = gtag;
      gtag('js', new Date());
      gtag('config', this.tagId);
      return new Promise<void>((resolve, reject) => {
        loader([{
          type: 'script',
          url: `${this.scriptScheme}www.googletagmanager.com/gtag/js?id=${this.tagId}`
        }], test, callback.bind(this, resolve, reject));
      });
    }
  
    // for subclasses to override with their own data layer code
    protected async initializeDataLayer(): Promise<void> {
      // Do nothing
    }

    protected async initializeGTag(): Promise<void> {
      // Do nothing
    }
  
    protected async eventsAvailable(count: number): Promise<void> {
      const events = this.consumeAllEvents();
      for (const event of events) {
        try {
          await this.processEvent(event);
        } catch (e) {
          console.error('GTagServiceProvider.eventsAvailable: exception in processEvent', e);
        }
      }
    }

    protected async processEvent(event: JanusAnalyticsEvent): Promise<void> {
      switch (event.category) {
        case "navigation": {
          switch (event.type) {
            case "page_view":
              this.updateDataLayer({
                "event": "pageviewCustomEvent",
                "pagePath": event.meta.page.path,
                "pageTitle": event.meta.page.name,
                "pageContent": event.meta.page.content
              });
              break;
          }
          break;
        }
        case "interaction": {
          this.updateDataLayer({
            "event": "interactionCustomEvent",
            "pagePath": event.meta.page.path,
            "pageTitle": event.meta.page.name,
            "pageContent": event.meta.page.content,
            "interactionType": event.type,
            "interactionTargetId": event.detail || ''
          });
          break;
        }
      }
    }
  }

  @injectable()
  export class BloomreachServiceProvider extends AbstractBufferingJanusAnalyticsServiceProvider {
    private started: boolean = false;
    private readonly exponea: any;
  
    public constructor() {
      super('Bloomreach');
      this.exponea = new Proxy((<any>window).exponea, {
        get(target, prop, receiver) {
          const origMethod = target[prop];
          if (typeof origMethod === 'function') {
            return function (...args) {
              //console.log('Exponea calling method', prop, { args });
              return origMethod.apply(target, args);
            }
          } else {
            return Reflect.get(target, prop, receiver);
          }
        }
      });
    }

    protected async initialize(): Promise<void> {
      if (window.localStorage && window.localStorage.getItem('cookie_consent') === 'true') {
        this.start(true);
      }
    }

    private start(hadPriorConsent: boolean) {
      this.exponea.start({
        track: {
          visits: false,
        },
        spa_reloading: {
          on_hash_change: true,
          on_url_change: true,
          banners: true,
          experiments: true,
          tags: true,
          visits: false,
          automatic_tracking: true,
        }
      });
      this.started = true;
    }

    protected handleTrackingConsentChange(optIn: boolean) {
      if (this.started && !optIn) {
        // stop?
      } else if (!this.started && optIn) {
        this.start(false);
      }
    }
  
    protected async eventsAvailable(count: number): Promise<void> {
      const events = this.consumeAllEvents();
      for (const event of events) {
        try {
          await this.processEvent(event);
        } catch (e) {
          console.error('BloomreachServiceProvider.eventsAvailable: exception in processEvent', e);
        }
      }
    }

    private massageProfile(profile: Record<string, string>): Record<string, string> {
      // profile data disabled for now
      return {};

      if (!profile) {
        profile = {};
      }
      try {
        if ('birth_date' in profile) {
          try {
            let dt = DateTime.fromISO(profile.birth_date, { zone: 'utc' });
            dt = dt.setZone('Europe/London');
            profile.birth_date = dt.toISO().substring(0, 10);
          } catch (e) {
            console.warn('Bloomreach: error parsing birthdate', e);
          }
        }
        delete profile.segment;
      } catch (e) {
        console.warn('Bloomreach: error massaging profile', e);        
      }
      return profile;
    }

    protected async processEvent(event: JanusAnalyticsEvent): Promise<void> {
      const trackConsent = (profile) => {
        // consent tracking disabled
        return;
        const action = (profile?.email_optin?.toString().toLowerCase() === 'true')? 'accept' : 'reject';
        for (const category of [ 'all', 'sms' ]) {
          this.exponea.track('consent', {
            action, category,
            timestamp: Math.round(new Date().getTime()/1000),
            valid_until: 'unlimited'
          });
        }
      };

      switch (event.category) {
        case "navigation": {
          switch (event.type) {
            case "page_view": {
              this.exponea.track('page_visit', {
                "path": event.meta.page.path,
                "title": event.meta.page.name,
                "content": event.meta.page.content
              });
              break;
            }
          }
          break;
        }
        case "interaction": {
          this.exponea.track('interaction', {
            "pagePath": event.meta.page.path,
            "pageTitle": event.meta.page.name,
            "pageContent": event.meta.page.content,
            "interactionType": event.type,
            "interactionTargetId": event.detail || ''
          });
          switch (event.type) {
            case 'login': {
              this.exponea.identify({ "yinz_id": event.detail, "email_id": event.context?.profile?.email }, this.massageProfile(event.context?.profile));
              if (event.context?.profile) {
                trackConsent(event.context?.profile);
              }
              break;
            }
            case 'profile': {
              // update profile disabled for now
              break;
              this.exponea.update(this.massageProfile(event.context?.profile));
              if (event.context?.profile) {
                trackConsent(event.context?.profile);
              }
              break;
            }
            case 'logout': {
              this.exponea.anonymize();
              break;
            }
          }
        }
      }
    }
  }
}